import React, { useEffect, useState } from "react";
import { ConfirmDialog, Dropdown, Text, DraggableCheckboxList, CheckboxListItem, depascalize } from "sprint-common-web";

import { ColumnConfig, AssetColumn, AssetListingConfig, sortColumnsByConfiguredOrder } from "./AssetListingConfig";

interface EditAssetListingColumnsConfigDialogProps {
    listingConfig: AssetListingConfig;
    saveListingConfig(listingConfig: AssetListingConfig): void;
    pageSize: number;
    setPageSize(pageSize: number): void;
    onClose(): void;
}

export const EditAssetListingColumnsConfigDialog = (props: EditAssetListingColumnsConfigDialogProps): JSX.Element => {
    const [items, setItems] = useState<CheckboxListItem[]>([]);
    const [pageSize, setPageSize] = useState<number>(props.pageSize);

    useEffect(() => {
        const checkboxListItems = getCheckboxListItems(props.listingConfig);

        setItems(checkboxListItems);
    }, []);

    const handleConfirm = () => {
        props.setPageSize(pageSize);

        const config = mapToListingConfig(items, pageSize);

        props.saveListingConfig(config);

        props.onClose();
    };

    const handlePageSizeChange = (key: string) => {
        setPageSize(Number(key));
    };

    return (
        <ConfirmDialog
            title={"Listing configuration"}
            text={""}
            confirm={{
                onClick: handleConfirm
            }}
            cancel={{
                onClick: props.onClose
            }}
            width={700}
            contentHeight={400}
        >
            <div style={{ display: "flex" }}>
                <div style={{ width: "50%", float: "left", left: 0, paddingRight: 20 }}>
                    <Text value="Page size" styles={{ root: { paddingBottom: 10 } }} />
                    <Dropdown
                        options={pageSizeDropDownOptions.map((key) => ({ key, text: key }))}
                        selectedKey={String(pageSize)}
                        onChange={handlePageSizeChange}
                    />
                </div>
                <div style={{ width: "50%", float: "right" }}>
                    <Text value="Columns" styles={{ root: { paddingBottom: 10 } }} />
                    <DraggableCheckboxList items={items} setItems={setItems} />
                </div>
            </div>
        </ConfirmDialog>
    );
};

const pageSizeDropDownOptions: string[] = ["10", "20", "50", "100"];

const getCheckboxListItems = (config: AssetListingConfig): CheckboxListItem[] => {
    return sortColumnsByConfiguredOrder(config.columns).map(([key, column]) => {
        const resolvedKey = Number(key);

        return {
            key: resolvedKey,
            text: normalizeColumnName(depascalize(AssetColumn[resolvedKey])),
            checked: column.visible,
            disabled: false
        };
    });
};

const mapToListingConfig = (columnItems: CheckboxListItem[], pageSize: number): AssetListingConfig => {
    const columns = {} as Record<AssetColumn, ColumnConfig>;

    let order = 0;

    for (const item of columnItems) {
        const index = Number(item.key) as AssetColumn;
        columns[index] = { order, visible: item.checked };

        order++;
    }

    return {
        pageSize,
        columns
    };
};

const normalizeColumnName = (name: string) =>
    name
        .split(" ")
        .map((word, idx) => (idx === 0 ? word : word.toLocaleLowerCase()))
        .join(" ");
